import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCrown,
  faCheck,
  faClock,
  faTriangleExclamation,
  faArrowRotateRight,
  faImage,
} from "@fortawesome/free-solid-svg-icons";

/**
 * PehchaanSetu design system — one small shared file so every screen (Home,
 * Circles, Chapters, Profiles, Admin, Vision) pulls from the same palette,
 * type scale and building blocks instead of re-inventing card styles per
 * file. Dark-first, mobile-first: text is intentionally small on phones and
 * steps up on lg: screens.
 *
 * Palette (see PSHome.jsx header comment for the full rationale):
 *   base    #0F0B1A   surface  white/[0.04]   border  white/10
 *   text    white/90  muted    white/55
 *   signature gradient: violet-500 -> fuchsia-500 -> rose-500
 *   live/success: emerald   pending: amber   director/role: violet
 */

export const ui = {
  page: "min-h-screen bg-[#0F0B1A] text-white",
  card: "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm",
  cardHover: "transition hover:bg-white/[0.06] hover:border-white/20",
  input:
    "w-full rounded-xl border border-white/10 bg-white/[0.06] px-3.5 py-2.5 text-[13px] lg:text-sm text-white placeholder:text-white/35 focus:border-violet-400/60 focus:outline-none focus:ring-2 focus:ring-violet-400/20 transition",
  label: "block text-[11px] lg:text-xs font-semibold text-white/50 tracking-wide",
  heading: "font-bold text-white",
  sub: "text-white/55",
  gradientText:
    "bg-gradient-to-r from-violet-300 via-fuchsia-300 to-rose-300 bg-clip-text text-transparent",
  gradientBtn:
    "bg-gradient-to-r from-violet-500 via-fuchsia-500 to-rose-500 text-white shadow-lg shadow-fuchsia-500/20 hover:shadow-fuchsia-500/30 hover:brightness-110",
  ghostBtn:
    "border border-white/15 bg-white/[0.03] text-white/80 hover:bg-white/[0.08] hover:text-white",
};

export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`rounded-full w-fit px-3.5 py-1.5 text-[11px] lg:text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${ui.gradientBtn} ${className}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`rounded-full w-fit px-3.5 py-1.5 text-[11px] lg:text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed ${ui.ghostBtn} ${className}`}
    >
      {children}
    </button>
  );
}

export function StatusPill({ tone = "neutral", icon, children }) {
  const tones = {
    live: "bg-emerald-400/10 text-emerald-300 ring-1 ring-emerald-400/30",
    pending: "bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/30",
    role: "bg-violet-400/10 text-violet-300 ring-1 ring-violet-400/30",
    danger: "bg-rose-400/10 text-rose-300 ring-1 ring-rose-400/30",
    neutral: "bg-white/[0.06] text-white/60 ring-1 ring-white/10",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] lg:text-[11px] font-semibold ${tones[tone]}`}
    >
      {icon && <FontAwesomeIcon icon={icon} className="text-[9px]" />}
      {children}
    </span>
  );
}

export function DirectorBadge() {
  return (
    <StatusPill tone="role" icon={faCrown}>
      Director
    </StatusPill>
  );
}

export function MemberBadge() {
  return (
    <StatusPill tone="live" icon={faCheck}>
      Member
    </StatusPill>
  );
}

export function PendingBadge({ children = "Requested" }) {
  return (
    <StatusPill tone="pending" icon={faClock}>
      {children}
    </StatusPill>
  );
}

export function EmptyState({ icon, title, hint }) {
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-center">
      {icon && <FontAwesomeIcon icon={icon} className="text-2xl text-white/20" />}
      <p className="text-[13px] lg:text-sm font-semibold text-white/70">{title}</p>
      {hint && <p className="max-w-xs text-[11px] lg:text-xs text-white/40">{hint}</p>}
    </div>
  );
}

// Distinguishes "nothing here yet" (normal, quiet) from "something actually
// broke" (needs a retry) — the raw `e.message` string used everywhere before
// this made every error, including a plain 404, look like a crash.
export function ErrorState({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl border border-rose-400/20 bg-rose-400/[0.06] px-4 py-6 text-center">
      <FontAwesomeIcon icon={faTriangleExclamation} className="text-lg text-rose-300" />
      <p className="text-[12px] lg:text-sm font-semibold text-rose-200">
        {message || "Something went wrong."}
      </p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-rose-300/30 px-3 py-1 text-[11px] font-semibold text-rose-200 hover:bg-rose-400/10"
        >
          <FontAwesomeIcon icon={faArrowRotateRight} className="text-[10px]" />
          Try again
        </button>
      )}
    </div>
  );
}

export function Notice({ children }) {
  return (
    <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.08] px-3.5 py-2.5 text-[12px] lg:text-sm text-emerald-200">
      {children}
    </div>
  );
}

// Role helpers — previously re-implemented inline (differently-formatted)
// in PSChapter, PSCircles and the pinned-circle block of PSCircles.
export const idEq = (a, b) => !!a && !!b && String(a) === String(b);

export function getRole(entity, userId) {
  const directorId = entity?.directorUserId?._id || entity?.directorUserId;
  const isDirector = idEq(directorId, userId);
  const isMember = (entity?.memberIds || []).some((m) => idEq(m?._id || m, userId));
  const isPending = (entity?.pendingRequestIds || []).some((m) => idEq(m?._id || m, userId));
  return { isDirector, isMember, isPending };
}

// memberIds/pendingRequestIds are now populated with { _id, name, think5whyscode }
// where the backend supports it — falls back to a truncated id if not.
export function memberLabel(m) {
  if (m && typeof m === "object") {
    return m.name || m.think5whyscode || `Member ${String(m._id).slice(-6)}`;
  }
  return `Member ${String(m).slice(-6)}`;
}

export function memberId(m) {
  return m?._id || m;
}

export function memberCountLabel(entity) {
  const n = (entity?.memberIds || []).length;
  return `${n} member${n === 1 ? "" : "s"}`;
}

/**
 * Banner / avatar image that degrades gracefully. PehchaanSetu never stores
 * an image URL in the DB — filenames are deterministic (<code>.webp), so a
 * banner that was never uploaded simply 404s and we fall back to an
 * initials tile instead of showing a broken image or an error.
 */
export function EntityThumb({ src, name, shape = "square", size = "md" }) {
  const [failed, setFailed] = useState(false);
  const initials = (name || "?").trim().charAt(0).toUpperCase();
  const sizes = {
    sm: "h-10 w-10 text-sm",
    md: "h-14 w-14 text-lg",
    lg: "h-16 w-16 text-xl",
  };
  const radius = shape === "circle" ? "rounded-full" : "rounded-xl";

  if (!src || failed) {
    return (
      <div
        className={`flex ${sizes[size]} shrink-0 items-center justify-center ${radius} bg-gradient-to-br from-violet-500/30 to-rose-500/30 font-bold text-white/80 ring-1 ring-white/10`}
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name || ""}
      onError={() => setFailed(true)}
      className={`${sizes[size]} shrink-0 ${radius} object-cover ring-1 ring-white/10`}
    />
  );
}

export function BannerImage({ src, name, className = "" }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div
        className={`flex items-center justify-center bg-gradient-to-br from-violet-600/25 via-fuchsia-600/20 to-rose-500/25 ${className}`}
      >
        <FontAwesomeIcon icon={faImage} className="text-white/20 text-xl" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name || ""}
      onError={() => setFailed(true)}
      className={`object-cover ${className}`}
    />
  );
}
